/**
 * Program Impact dashboard (PID) readers shared by the School and Teacher pages.
 *
 * Pure: no React, no MUI. The card readers were moved as-is from
 * SchoolDashboard/pages/ProgramImpact.page.jsx (only toNumber was renamed to
 * toFiniteNumber — units.js already exports a toNumber that behaves differently).
 * Curriculum Coverage (SD-44-05) is not read: the Data Map drops it (D-10).
 */
import {rowsFrom, isSuppressedKpi, isSuppressedBenchmark, SUPPRESSED_COPY} from './dashboardCards';

const PROGRAM_IMPACT_OVERVIEW_SECTION_CODE = 'S_1';
const PROGRAM_IMPACT_BY_CLASS_SECTION_CODE = 'S_2';

/** Card sections requested from the Program Impact dashboard. */
export const PROGRAM_IMPACT_SECTIONS = [
    PROGRAM_IMPACT_OVERVIEW_SECTION_CODE,
    PROGRAM_IMPACT_BY_CLASS_SECTION_CODE,
];

/** Post-score a learner needs to pass, and the pass rate a class needs to pass. */
export const PASS_THRESHOLD = 80;
/** Tooltip name of the pass-threshold marker on the by-class pass-rate bars. */
export const PASS_THRESHOLD_LABEL = 'Pass threshold';

/** A number or numeric string as a number; anything else (including "80%") is null. */
export const toFiniteNumber = (v) => (typeof v === 'number' ? v : (/^-?\d+(\.\d+)?$/.test(String(v ?? '').trim()) ? Number(v) : null));

/** A KPICard's add-info value by name (case-insensitive); undefined when absent. */
export const addInfo = (card, name) => card?.kpi_add_infos?.find((i) => String(i.info_name || '').toLowerCase() === name.toLowerCase())?.info_value;

/** A value as a rounded percentage ("+12%" when signed and positive); "—" when not numeric. */
export const pctText = (v, signed = false) => {
    const n = toFiniteNumber(v);
    if (n === null) return '—';
    const r = Math.round(n);
    return `${signed && r > 0 ? '+' : ''}${r}%`;
};

/** SD-44-03 … 07 from PID.S_1 C_2, C_3, C_5, C_6 — with the mockup's labels. */
export const kpisFrom = (cards) => {
    const c2 = cards.find((c) => c.card_code === 'C_2');
    const c3 = cards.find((c) => c.card_code === 'C_3');
    const c5 = cards.find((c) => c.card_code === 'C_5');
    const c6 = cards.find((c) => c.card_code === 'C_6');
    const participated = String(c2?.kpi_value ?? '').match(/^(\S+)\s+of\s+(\S+)$/);
    const completed = addInfo(c3, 'Completed');
    const enrolled = addInfo(c3, 'Enrolled');
    const confidence = addInfo(c5, 'Evidence confidence');
    return [
        {
            label: 'Learners Participated',
            value: participated ? participated[1] : String(c2?.kpi_value ?? '—'),
            sub: participated ? `of ${participated[2]} enrolled` : undefined,
        },
        {
            label: 'Program Completion',
            value: pctText(c3?.kpi_value),
            sub: completed !== undefined && enrolled !== undefined ? `${completed} / ${enrolled} completed` : undefined,
        },
        // Learning scores under the n floor (fewer than 10 learners with pre and post) arrive flagged and empty.
        {label: 'Avg Impact Rate', value: isSuppressedKpi(c5) ? '—' : pctText(c5?.kpi_value, true), sub: isSuppressedKpi(c5) ? SUPPRESSED_COPY : (confidence ? `Evidence confidence: ${confidence}` : undefined)},
        {label: 'Avg Pass Rate*', value: isSuppressedKpi(c6) ? '—' : pctText(c6?.kpi_value), sub: isSuppressedKpi(c6) ? SUPPRESSED_COPY : `post-score ≥ ${PASS_THRESHOLD}`},
    ];
};

/** SD-44-08 / 09 — one row per class from PID.S_2 C_2 "Impact by class". A class under the n floor keeps its
    row (its counts are real) but its scores are flagged — the row renders the placeholder. */
export const classRowsFrom = (card) => rowsFrom(card, {
    grade: 'Grade',
    klass: 'Class',
    pre: ['Avg. Pre', 'Avg. Pre-Assessment'],
    post: ['Avg. Post', 'Avg. Post-Assessment'],
    impact: ['Avg. Impact Rate %', 'Avg. Impact Rate'],
    passRate: ['Pass Rate %', 'Pass Rate'],
    status: ['Status (Pass / Not Pass)', 'Status'],
}).map((r) => ({
    label: [r.klass, r.grade].filter((v) => v && v !== '—').join(' · ') || '—',
    suppressed: !!(r._suppressed.pre || r._suppressed.post || r._suppressed.impact || r._suppressed.passRate),
    pre: toFiniteNumber(r._raw.pre),
    post: toFiniteNumber(r._raw.post),
    impact: pctText(r._raw.impact, true),
    passRate: toFiniteNumber(r._raw.passRate),
    status: r.status,
}));

/** The PID response's S_1 / S_2 cards, program meta, class table and school-average post; null without sections. */
export const programImpactMetaFrom = (data) => {
    const sections = data?.cards?.sections;
    if (!Array.isArray(sections)) return null;
    const overviewCards = sections.find((sec) => sec.section_code === PROGRAM_IMPACT_OVERVIEW_SECTION_CODE)?.cards || [];
    const byClassCards = sections.find((sec) => sec.section_code === PROGRAM_IMPACT_BY_CLASS_SECTION_CODE)?.cards || [];
    const meta = overviewCards.find((c) => c.card_code === 'C_1') || null;
    const classTable = byClassCards.find((c) => c.card_code === 'C_2') || null;
    const schoolAvgBench = byClassCards.find((c) => c.card_code === 'C_1')?.items?.[0]?.benchmark_values?.find((b) => /school avg/i.test(b.name || ''));
    const schoolAvgPost = !schoolAvgBench || isSuppressedBenchmark(schoolAvgBench) ? null : toFiniteNumber(schoolAvgBench.value);
    return {overviewCards, byClassCards, meta, classTable, schoolAvgPost};
};

// Missing or unparsable dates sort last.
const timeOf = (v) => {
    const t = v === null || v === undefined || v === '' ? NaN : new Date(v).getTime();
    return Number.isNaN(t) ? -Infinity : t;
};

/** The program's most recently updated dashboard (ties keep list order); null when it has none. */
export const defaultDashboardFor = (program) => {
    const list = Array.isArray(program?.dashboards) ? program.dashboards : [];
    return list.reduce((best, d) => (best === null || timeOf(d?.updatedAt) > timeOf(best?.updatedAt) ? d : best), null);
};

/** Institute choices from the program's dashboards and the response's available_institutes, deduped, newest first. */
export const instituteOptionsFrom = (program, availableInstitutes) => {
    const all = [
        ...(Array.isArray(program?.dashboards) ? program.dashboards : []),
        ...(Array.isArray(availableInstitutes) ? availableInstitutes : []),
    ];
    const seen = new Set();
    const options = [];
    all.forEach((item) => {
        const code = item?.institute_code || '';
        if (seen.has(code)) return;
        seen.add(code);
        options.push({
            institute_code: code,
            label: code === '' ? 'Default institute' : code,
            updatedAt: item?.updatedAt ?? null,
            has_data: item?.has_data ?? null,
        });
    });
    return options
        .map((o, i) => ({o, i}))
        .sort((a, b) => (timeOf(b.o.updatedAt) - timeOf(a.o.updatedAt)) || (a.i - b.i))
        .map(({o}) => o);
};

/** Secondary text for a program row in the list; null when there is nothing to say (unknown is not "visible"). */
export const programListHintFor = (program, dashboard) => {
    if (!program?.has_dashboard) return 'No dashboard';
    if (dashboard?.has_data === false) return 'No data yet';
    if (dashboard?.scope_suppressed === true) return 'Scores hidden (n < 10)';
    return null;
};

/** 'rejected' when the API refused access to the dashboard, otherwise 'failed'. */
export const classifyDashboardError = (err) => {
    if (err?.errorField === 'error_message') return 'rejected';
    if (err?.status === 400 && /insufficient access/i.test(err?.message || '')) return 'rejected';
    return 'failed';
};
